export class HttpError extends Error {
  #status: number;
  #headers: Headers;

  constructor(response: Response) {
    const message =
      response.statusText && response.statusText.trim().length > 0
        ? response.statusText.trim()
        : `HTTP ${response.status}`;
    super(message);
    this.name = "HttpError";
    this.#status = response.status;
    this.#headers = response.headers;
  }

  get status() {
    return this.#status;
  }

  get headers() {
    return this.#headers;
  }
}

export class ResponseError extends Error {
  #status: number;
  #headers: Headers;

  constructor(message: string, response: Response, options?: ErrorOptions) {
    super(message, options);
    this.name = "ResponseError";
    this.#status = response.status;
    this.#headers = response.headers;
  }

  get status() {
    return this.#status;
  }

  get headers() {
    return this.#headers;
  }
}

export type ApiShape = {
  [url: string]: {
    [method: string]: {
      request: {
        url: string;
        method: string;
        headers?: Record<string, string>;
        body?: unknown;
        query?: Record<string, string | number | boolean | undefined>;
        path?: Record<string, string | number>;
      };
      response: {
        status: number;
        statusText?: string;
        headers?: Record<string, string>;
        body?: unknown;
      };
    };
  };
};

export type ApiRequest<
  Api extends ApiShape,
  Url extends keyof Api,
  Method extends keyof Api[Url],
> = Api[Url][Method]["request"];

export type ApiResponse<
  Api extends ApiShape,
  Url extends keyof Api,
  Method extends keyof Api[Url],
> = Exclude<
  Api[Url][Method]["response"],
  | { status: 401 }
  | { status: 403 }
  | { status: 429 }
  | { status: 500 }
  | { status: 501 }
  | { status: 502 }
  | { status: 503 }
  | { status: 504 }
  | { status: 505 }
  | { status: 506 }
  | { status: 507 }
  | { status: 508 }
  | { status: 510 }
  | { status: 511 }
>;

const parseResponseBody = async (
  response: Response,
  contentType: string | undefined,
): Promise<unknown> => {
  if (response.status === 204 || response.body === null) {
    return undefined;
  }

  if (contentType !== "application/json") {
    return await response.blob();
  }

  const responseText = await response.text();

  if (responseText.length === 0) {
    throw new ResponseError(
      `Empty response body with status ${response.status}. Use the 204 status code if body is not needed`,
      response,
    );
  }

  try {
    return JSON.parse(responseText) as unknown;
  } catch (error) {
    throw new ResponseError(
      `Failed to parse JSON response (HTTP ${response.status})`,
      response,
      { cause: error },
    );
  }
};

export const fetcher = async <
  Api extends ApiShape,
  Url extends keyof Api,
  Method extends keyof Api[Url],
>({
  url,
  query,
  path,
  headers,
  body,
  method,
  baseUrl,
  fetchModifier,
}: Api[Url][Method]["request"] & {
  baseUrl: string;
  fetchModifier?: (request: Request) => Response | Promise<Response>;
}): Promise<ApiResponse<Api, Url, Method>> => {
  const searchParams = new URLSearchParams(
    query &&
      Object.entries(query).flatMap(([key, value]) =>
        value !== undefined ? [[key, value.toString()]] : [],
      ),
  );
  const urlWithPath = path
    ? url.replaceAll(/\{(\w+)\}/g, (_, key) => {
        return path[key]!.toString();
      })
    : url;

  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : baseUrl + "/";
  const normalizedPathUrl = urlWithPath.startsWith("/")
    ? urlWithPath.slice(1)
    : urlWithPath;
  const normalizedSearch = searchParams.size ? `?${searchParams}` : "";

  const finalUrl = `${normalizedBaseUrl}${normalizedPathUrl}${normalizedSearch}`;

  const request = new Request(finalUrl, {
    headers,
    body:
      body !== undefined && headers?.["content-type"] === "application/json"
        ? JSON.stringify(body)
        : (body as BodyInit),
    method,
  });

  const response = await (fetchModifier
    ? fetchModifier(request)
    : fetch(request));

  if (
    response.status === 401 ||
    response.status === 403 ||
    response.status === 429 ||
    response.status >= 500
  ) {
    throw new HttpError(response);
  }

  const contentType = response.headers
    .get("content-type")
    ?.split(";")[0]
    ?.trim();

  const responseBody = await parseResponseBody(response, contentType);

  return {
    status: response.status,
    statusText: response.statusText,
    headers: Object.fromEntries(response.headers),
    body: responseBody,
  } as ApiResponse<Api, Url, Method>;
};
